import type { TextDetection } from './types';

/**
 * Names dictionary detector. Finds capitalized name-shaped pairs in the
 * source text and confirms via a curated list of common US given names
 * and surnames. Runs alongside regex + forms + NER as a third recall
 * layer; especially useful for narrative text where NER misses names.
 *
 * Confidence is based on how much of the candidate matches the dictionary:
 *   - first + last both in dict       → 0.85
 *   - only first in dict              → 0.6 (could be name, could be word)
 *   - only last in dict               → 0.55
 *   - neither                         → dropped
 */

type LoadedDicts = {
  first: ReadonlySet<string>;
  last: ReadonlySet<string>;
};

let dictsPromise: Promise<LoadedDicts> | null = null;

const loadDicts = async (): Promise<LoadedDicts> => {
  dictsPromise ??= (async (): Promise<LoadedDicts> => {
    const mod = await import('./names-data');
    return { first: mod.FIRST_NAMES, last: mod.LAST_NAMES };
  })();
  return dictsPromise;
};

/** Words that look like names but should not trigger detection. */
const FALSE_POSITIVE_WORDS: ReadonlySet<string> = new Set([
  // Place + business suffixes
  'united',
  'states',
  'america',
  'new',
  'york',
  'los',
  'angeles',
  'san',
  'francisco',
  'pennsylvania',
  'avenue',
  'street',
  'road',
  'boulevard',
  'court',
  'lane',
  'drive',
  'place',
  'company',
  'corporation',
  'incorporated',
  'limited',
  'llc',
  'inc',
  'corp',
  'co',
  'ltd',
  // Sentence-starter words that often appear capitalised at the
  // beginning of a wrapped line. Without these, allowing newline in
  // the run separator picks up false pairs like "Bob\nFor", "Smith\nThe".
  'the',
  'a',
  'an',
  'and',
  'or',
  'but',
  'for',
  'so',
  'yet',
  'nor',
  'if',
  'when',
  'while',
  'as',
  'because',
  'since',
  'until',
  'before',
  'after',
  'this',
  'that',
  'these',
  'those',
  'such',
  'then',
  'thus',
  'hence',
  'however',
  'therefore',
  'moreover',
  'meanwhile',
  // Lease / legal-doc boilerplate capitalised words that aren't names
  // but turn up Title-Case mid-document.
  'tenant',
  'landlord',
  'lessor',
  'lessee',
  'agreement',
  'premises',
  'witness',
  'signature',
  'date',
  'section',
]);

const isAllUpper = (s: string): boolean => s === s.toUpperCase() && /[A-Z]/.test(s);

const looksNameLike = (s: string): boolean => {
  // Must start with a capital and have only letters / hyphens / apostrophes.
  if (!/^[A-Z][a-zA-Z'-]*$/.test(s) && !isAllUpper(s)) return false;
  if (s.length < 2 || s.length > 30) return false;
  if (FALSE_POSITIVE_WORDS.has(s.toLowerCase())) return false;
  return true;
};

type NameMatch = {
  text: string;
  start: number;
  end: number;
  firstHit: boolean;
  lastHit: boolean;
};

/**
 * Walk the text finding capitalized pairs and verify each pair against
 * the dictionary. Returns matches with hit flags so the caller can
 * compute confidence.
 *
 * Implementation: find every run of 2+ adjacent Title-Case (or ALL-CAPS)
 * tokens, then slide 2- and 3-token windows across the run. The earlier
 * version emitted one detection for the entire run, which let real name
 * pairs hide inside longer runs like a label or column header followed
 * by the actual values — e.g. "Name Marcus Chen Priya Raghavan" from a
 * table row would only check (Name, Raghavan) and drop everything.
 */
const findCandidates = (text: string, dicts: LoadedDicts): NameMatch[] => {
  const out: NameMatch[] = [];
  const seen = new Set<string>();
  // A run is 2+ adjacent capitalised tokens. Separator is space, NBSP,
  // or newline — newlines included so a name wrapped across a line
  // break ("John\nSmith") still resolves. Tabs are intentionally NOT
  // included; the form-field detector keys off tabs as column
  // boundaries and we don't want values in different table cells
  // joined into a single name candidate.
  const runRe =
    /\b(?:[A-Z][A-Za-z'-]+|[A-Z]{2,})(?:[ \u00A0\n]+(?:[A-Z][A-Za-z'-]+|[A-Z]{2,}|[A-Z]\.)){1,5}\b/g;
  const isInitial = (s: string): boolean => /^[A-Z]\.?$/.test(s);
  let m: RegExpExecArray | null;
  while ((m = runRe.exec(text)) !== null) {
    const matched = m[0];
    const runStart = m.index;
    // Re-tokenise with positions so each window can carry its own char range.
    type Tok = { text: string; start: number };
    const tokens: Tok[] = [];
    const tokRe = /[A-Z](?:\.|[A-Za-z'-]*)/g;
    let t: RegExpExecArray | null;
    while ((t = tokRe.exec(matched)) !== null) {
      tokens.push({ text: t[0], start: runStart + t.index });
    }
    if (tokens.length < 2) continue;
    if (!tokens.every((tk) => isInitial(tk.text) || looksNameLike(tk.text))) continue;

    type Window = { a: Tok; b: Tok; firstHit: boolean; lastHit: boolean };
    const windows: Window[] = [];
    const tryWindow = (a: Tok | undefined, b: Tok | undefined): void => {
      if (!a || !b) return;
      if (isInitial(a.text) || isInitial(b.text)) return;
      const firstHit = dicts.first.has(a.text.toLowerCase());
      const lastHit = dicts.last.has(b.text.toLowerCase());
      if (!firstHit && !lastHit) return;
      windows.push({ a, b, firstHit, lastHit });
    };

    // Try every window size from longest to shortest. The longest
    // possible name is the entire run; the shortest is a 2-token pair.
    // Walking longest-first lets us suppress shorter windows that sit
    // fully inside a stronger longer window — e.g. for
    // "John Smith Robert Davis" we keep one detection covering
    // (John, Davis) rather than emitting every internal sub-pair.
    for (let span = tokens.length - 1; span >= 1; span--) {
      for (let i = 0; i + span < tokens.length; i++) {
        tryWindow(tokens[i], tokens[i + span]);
      }
    }

    // Prefer windows where BOTH ends hit the dictionary. Among those,
    // prefer the longest. Among equal-length, earliest. Then suppress
    // any remaining window that's fully contained inside a kept window
    // — those are redundant in the sidebar.
    windows.sort((x, y) => {
      const xBoth = x.firstHit && x.lastHit ? 1 : 0;
      const yBoth = y.firstHit && y.lastHit ? 1 : 0;
      if (xBoth !== yBoth) return yBoth - xBoth;
      const xLen = x.b.start + x.b.text.length - x.a.start;
      const yLen = y.b.start + y.b.text.length - y.a.start;
      if (xLen !== yLen) return yLen - xLen;
      return x.a.start - y.a.start;
    });

    const kept: Window[] = [];
    for (const w of windows) {
      const ws = w.a.start;
      const we = w.b.start + w.b.text.length;
      const contained = kept.some((k) => {
        const ks = k.a.start;
        const ke = k.b.start + k.b.text.length;
        return ks <= ws && ke >= we;
      });
      if (!contained) kept.push(w);
    }

    for (const w of kept) {
      const start = w.a.start;
      const end = w.b.start + w.b.text.length;
      const key = `${String(start)}-${String(end)}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({
        text: text.slice(start, end),
        start,
        end,
        firstHit: w.firstHit,
        lastHit: w.lastHit,
      });
    }
  }
  return out;
};

const confidenceFor = (firstHit: boolean, lastHit: boolean): number => {
  if (firstHit && lastHit) return 0.85;
  // Partial-hit floors bumped so the sidebar never surfaces a sub-60 %
  // candidate. Surname-only is the noisiest of the three (lots of
  // English nouns / colors / places double as surnames — Brown, Hill,
  // Stone, Black) so it lands fractionally below first-name-only.
  if (firstHit) return 0.65;
  return 0.6;
};

/** Main entry. Lazy-loads the dictionary on first call. */
export const detectNames = async (text: string): Promise<TextDetection[]> => {
  const dicts = await loadDicts();
  const candidates = findCandidates(text, dicts);
  return candidates.map((c) => ({
    type: 'name' as const,
    text: c.text,
    start: c.start,
    end: c.end,
    confidence: confidenceFor(c.firstHit, c.lastHit),
    source: 'regex' as const,
  }));
};

/** Test-only export of the candidate finder using ad-hoc dicts. */
export const _testFindCandidates = findCandidates;
